import { coerceHallThemeId } from "./themes";
import { clampMixSeconds, bindHallSyncPush, useHallStore, type HallSyncPayload, type Phase } from "./store";
import { setHallRole } from "./role";
import { resetPhaseClock } from "./timeline";

const CHANNEL = "bingo-blast-hall";
export const STAGE_PATH = "/stage";
export const STAGE_WINDOW = "bingo-blast-stage";
export const STAGE_HELLO = "bingo-stage-hello";

let channel: BroadcastChannel | null = null;

function getChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL);
  return channel;
}

function snapshot(): HallSyncPayload {
  const s = useHallStore.getState();
  return {
    remaining: s.remaining,
    called: s.called,
    current: s.current,
    phase: s.phase,
    theme: s.theme,
    mixSeconds: s.mixSeconds,
    voiceRepeats: s.voiceRepeats,
    voicePause: s.voicePause,
    quickCall: s.quickCall,
    tapSkip: s.tapSkip,
    liteMode: s.liteMode,
    displayLiteMode: s.displayLiteMode,
  };
}

function postSnapshot() {
  getChannel()?.postMessage({ type: "snapshot", state: snapshot() });
}

function applySnapshot(raw: HallSyncPayload) {
  const prev = useHallStore.getState().phase;
  const phase = raw.phase as Phase;
  useHallStore.setState({
    remaining: Array.isArray(raw.remaining) ? raw.remaining : [],
    called: Array.isArray(raw.called) ? raw.called : [],
    current: raw.current,
    phase,
    theme: coerceHallThemeId(raw.theme),
    mixSeconds: clampMixSeconds(raw.mixSeconds),
    voiceRepeats: Math.max(1, Math.min(5, Number(raw.voiceRepeats) || 1)),
    voicePause: Boolean(raw.voicePause),
    quickCall: Boolean(raw.quickCall),
    tapSkip: raw.tapSkip !== false,
    liteMode: Boolean(raw.liteMode),
    displayLiteMode:
      typeof raw.displayLiteMode === "boolean" ? raw.displayLiteMode : Boolean(raw.liteMode),
  });
  if (phase !== prev) resetPhaseClock(phase);
}

export function connectHallOperator() {
  setHallRole("operator");
  bindHallSyncPush(postSnapshot);
  const ch = getChannel();
  if (!ch) return () => bindHallSyncPush(null);
  const onMsg = (ev: MessageEvent) => {
    const data = ev.data;
    if (!data || data.type !== "hello") return;
    postSnapshot();
    window.dispatchEvent(new Event(STAGE_HELLO));
  };
  ch.addEventListener("message", onMsg);
  return () => {
    ch.removeEventListener("message", onMsg);
    bindHallSyncPush(null);
  };
}

export function connectHallDisplay(onClose?: () => void) {
  setHallRole("display");
  const ch = getChannel();
  if (!ch) return () => undefined;
  const onMsg = (ev: MessageEvent) => {
    const data = ev.data;
    if (!data) return;
    if (data.type === "close") {
      window.clearInterval(timer);
      ch.removeEventListener("message", onMsg);
      const finish = () => {
        try {
          window.close();
        } catch {
          /* browser blocked */
        }
        onClose?.();
      };
      if (document.fullscreenElement) {
        void document.exitFullscreen().then(finish).catch(finish);
        return;
      }
      finish();
      return;
    }
    if (data.type !== "snapshot" || !data.state) return;
    applySnapshot(data.state as HallSyncPayload);
  };
  ch.addEventListener("message", onMsg);
  const hello = () => ch.postMessage({ type: "hello" });
  hello();
  const timer = window.setInterval(hello, 2000);
  return () => {
    window.clearInterval(timer);
    ch.removeEventListener("message", onMsg);
  };
}

export function openStageWindow() {
  const url = new URL(STAGE_PATH, window.location.origin).toString();
  return window.open(url, STAGE_WINDOW);
}

export function requestStageClose(win: Window | null) {
  getChannel()?.postMessage({ type: "close" });
  if (!win) return;
  try {
    win.close();
  } catch {
    /* ignore */
  }
}
